<template>
  <div class="flex flex-col">
    <div class="border border-gray-200 p-4 rounded-md text-gray-800 text-sm">
      <div v-html="transformContent(comment.contents)"></div>

      <div class="grid grid-cols-3 gap-2 mt-2" v-if="getDocuments(comment) > 0">
        <div
          v-for="document in getDocuments(comment)"
          :key="document.id"
          class="cursor-pointer flex space-x-1 rounded-md border flex items-center justify-center bg-white p-2 overflow-hidden"
          @click="downloadDocument(document.urls.file)"
        >
          <FileFive class="w-6 h-6 shrink-0" />
          <span class="truncate text-ellipsis text-gray-700 font-medium">
            {{ document.name }}
          </span>
        </div>
      </div>

      <div class="flex gap-2 mt-2" v-if="commentImages.length > 0">
        <div v-for="image in commentImages" :key="image.id">
          <img
            class="rounded-xl w-[86px] h-[86px] object-cover aspect-square"
            :src="image.urls.thumbnail"
            alt=""
            @click="previewCommentImage(image.urls.large)"
          />
        </div>
      </div>
    </div>

    <time-ago
      relative
      :date="fromUTC(comment.created_at)"
      :locale="locale"
      class="text-xs text-gray-800 font-medium self-end mt-1"
    />

    <MediaViewer
      :open="openImageViewer"
      :url="imageBeingViewed"
      @close="openImageViewer = false"
      @next="nextCommentImage"
      @previous="previousCommentImage"
      :multiple="commentImages.length > 1"
    />
  </div>
</template>

<script setup>
import { defineProps, ref, computed } from "vue";
import FileFive from "@/Tailwind/Icon/FileFive.vue";
import TimeAgo from "@/Tailwind/Components/TimeAgo.vue";
import MediaViewer from "@/Tailwind/Components/MediaViewer.vue";

import { useCookies } from "vue3-cookies";
import { usePage } from "@inertiajs/vue3";
import { useUTC } from "@/Tailwind/Mixins/useUTC";

import { generateHTML } from "@tiptap/html";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";

const props = defineProps({
  comment: Object,
});

const commentImages = computed(() => {
  const files = props.comment?.documents;
  if (!files) {
    return [];
  }

  return props.comment?.documents.filter((document) =>
    ["image/png", "image/jpg", "image/jpeg"].includes(document.mime_type)
  );
});

const imageBeingViewed = ref("");
const openImageViewer = ref(false);

const { cookies } = useCookies();
const { fromUTC } = useUTC();

const defaultLocale = usePage().props.locale;
const locale = cookies.get("messenger_locale") || defaultLocale;

function downloadDocument(url) {
  window.open(url, "_blank");
}

function previewCommentImage(url) {
  imageBeingViewed.value = url;
  openImageViewer.value = true;
}

const nextCommentImage = () => {
  const index = commentImages.value.findIndex(
    (photo) => photo.urls.large === imageBeingViewed.value
  );

  if (index === commentImages.value.length - 1) {
    imageBeingViewed.value = commentImages.value[0].urls.large;
  } else {
    imageBeingViewed.value = commentImages.value[index + 1].urls.large;
  }
};

const previousCommentImage = () => {
  const index = commentImages.value.findIndex(
    (photo) => photo.urls.large === imageBeingViewed.value
  );

  if (index === 0) {
    imageBeingViewed.value =
      commentImages.value[commentImages.value.length - 1].urls.large;
  } else {
    imageBeingViewed.value = commentImages.value[index - 1].urls.large;
  }
};

function getDocuments(comment) {
  const files = comment?.documents;
  if (!files) {
    return [];
  }

  return comment?.documents.filter(
    (document) =>
      !["image/png", "image/jpg", "image/jpeg"].includes(document.mime_type)
  );
}

function transformContent(content) {
  return generateHTML(content, [StarterKit, Link, Mention]);
}
</script>
